import { Controller, useWatch } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import useTranslations from '../hooks/useTranslations';
import { LANG_OPTIONS } from '../constants/constants';

/**
 * Componente para gestionar traducciones (relaciones entre páginas en diferentes idiomas)
 *
 * @param {Object} props
 * @param {Array} props.translations
 * @param {Function} props.onAddTranslation
 * @param {Function} props.onRemoveTranslation
 * @param {Object} props.control
 * @param {string} props.currentLang
 * @param {string} props.currentPageId
 * @param {string} [props.idField='entry_page_id'] - field name for linked page id
 * @param {string} [props.endpoint='/entry_pages/translations']
 */
export const TranslationsManager = ({
  translations = [],
  onAddTranslation,
  onRemoveTranslation,
  control,
  currentLang,
  currentPageId,
  idField = 'entry_page_id',
  endpoint = '/entry_pages/translations',
}) => {
  const { allPages, loading, error } = useTranslations(endpoint);
  const watchedTranslations = useWatch({
    control,
    name: 'translations',
    defaultValue: translations,
  });

  const rows = Array.isArray(watchedTranslations)
    ? watchedTranslations
    : Array.isArray(translations)
      ? translations
      : [];

  const currentPageIdStr = currentPageId != null ? String(currentPageId) : '';

  /**
   * Filtra páginas por idioma seleccionado.
   * Incluye la página actual pero marcada como disabled.
   */
  const getPagesByLanguage = (lang) => {
    if (!lang || !allPages.length) return [];
    const langKey = String(lang).toUpperCase();

    return allPages
      .filter((page) => String(page.lang || '').toUpperCase() === langKey)
      .map((page) => {
        const id = page._id != null ? String(page._id) : '';
        const slugBit = page.full_slug || page.slug || '';
        const titleBit = page.title || page.hero?.title || page.country || '';
        return {
          ...page,
          _id: id,
          pageLabel: titleBit ? `${slugBit} — ${titleBit}` : slugBit || id,
          disabled: Boolean(id && id === currentPageIdStr),
        };
      });
  };

  /**
   * Idiomas disponibles para nuevas traducciones
   * (excluye idioma actual y los ya usados en filas).
   */
  const getAvailableLanguages = () => {
    if (!Array.isArray(LANG_OPTIONS)) return [];

    const usedLanguages = rows.filter((t) => t.lang).map((t) => t.lang);

    return LANG_OPTIONS.filter(
      (lang) => lang.code !== currentLang && !usedLanguages.includes(lang.code)
    );
  };

  if (loading) {
    return (
      <div
        className="flex align-items-center justify-content-center"
        style={{ minHeight: '200px' }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        <span className="ml-3">Loading translations...</span>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Message
          severity="error"
          text={`Error loading translations: ${error}`}
          className="mb-3"
        />
      )}

      {rows.length > 0 ? (
        <div className="mb-4">
          <DataTable
            value={rows}
            emptyMessage="No translations added yet"
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              header="Language"
              body={(rowData, options) => {
                const selectedLang = rowData.lang;
                const langOptionsWithLabel = LANG_OPTIONS.map((lang) => ({
                  ...lang,
                  disabled: lang.code === currentLang,
                  name:
                    lang.code === currentLang ? `${lang.name} (current)` : lang.name,
                }));

                return (
                  <Controller
                    name={`translations.${options.rowIndex}.lang`}
                    control={control}
                    defaultValue={selectedLang}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        options={langOptionsWithLabel}
                        optionLabel="name"
                        optionValue="code"
                        optionDisabled="disabled"
                        placeholder="Select language"
                        className="w-full"
                        showClear
                      />
                    )}
                  />
                );
              }}
              style={{ width: '180px' }}
            />
            <Column
              header="Page"
              body={(rowData, options) => {
                const currentRowLang = rows[options.rowIndex]?.lang;
                const availablePages = getPagesByLanguage(currentRowLang);

                return (
                  <Controller
                    name={`translations.${options.rowIndex}.${idField}`}
                    control={control}
                    defaultValue={
                      rowData[idField] != null ? String(rowData[idField]) : ''
                    }
                    render={({ field }) => (
                      <Dropdown
                        value={field.value != null ? String(field.value) : null}
                        options={availablePages}
                        optionLabel="pageLabel"
                        optionValue="_id"
                        placeholder="Search page..."
                        className="w-full"
                        filter
                        filterBy="pageLabel"
                        filterInputAutoFocus
                        showClear
                        optionDisabled="disabled"
                        emptyMessage={
                          currentRowLang
                            ? 'No pages found for this language'
                            : 'Select a language first'
                        }
                        disabled={!currentRowLang}
                        onChange={(e) => field.onChange(e.value || '')}
                      />
                    )}
                  />
                );
              }}
            />
            <Column
              header="Action"
              body={(rowData, options) => (
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-danger p-button-sm"
                  onClick={() => onRemoveTranslation(options.rowIndex)}
                  tooltip="Remove translation"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              style={{ width: '80px', textAlign: 'center' }}
            />
          </DataTable>
        </div>
      ) : null}

      <div className="flex justify-content-end mt-3">
        <Button
          type="button"
          label="Add Translation"
          icon="pi pi-plus"
          className="p-button-primary p-button-sm w-auto"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddTranslation();
          }}
          disabled={getAvailableLanguages().length === 0}
          tooltip={
            getAvailableLanguages().length === 0
              ? 'No more languages available. All languages already have translations.'
              : ''
          }
          tooltipOptions={{ position: 'top' }}
        />
      </div>
      {rows.length === 0 && (
        <div className="mt-3 p-4 border-round border-1 surface-border text-center text-color-secondary">
          <i className="pi pi-language text-2xl mb-2 block" style={{ opacity: 0.55 }} />
          <div className="font-medium text-color">No translations yet</div>
          <div className="text-sm mt-1">
            Add a language version of this page with the button above.
          </div>
        </div>
      )}
    </>
  );
};

export default TranslationsManager;
